/**
 * Bouwt de tokenprovider en houdt de authenticatiestaat bij.
 *
 * De provider wordt **buiten React** gemaakt, één keer per browsersessie. Dat is
 * geen stijlkeuze: MSAL mag maar één keer bestaan. Twee instanties zouden allebei
 * `handleRedirectPromise()` aanroepen op dezelfde autorisatiecode, waarvan er dan
 * één faalt — en `StrictMode` laat een component-initialisatie in ontwikkeling
 * bewust twee keer lopen. Er is ook maar één identiteit per tabblad, dus dit is
 * geen React-state.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { clearApiCache, setTokenProvider, setUnauthorizedHandler } from "@/api/client";
import { fetchMe } from "@/api/users";
import { resetDataVersion } from "@/lib/dataVersion";

import { AuthContext, type AuthState, type AuthStatus } from "./authState";
import {
  type AtlasAccount,
  type TokenProvider,
  configuredAuthMode,
  createAnonymousTokenProvider,
  createTokenProvider,
} from "./tokenProvider";

type Setup = {
  provider: TokenProvider;
  /** Gevuld als de gekozen modus niet gebouwd kón worden. */
  error: string | null;
};

let setup: Setup | null = null;

/**
 * Levert de provider van deze sessie; bouwt hem bij de eerste aanroep.
 *
 * De fout wordt hier gevangen en niet doorgegooid: `createTokenProvider()`
 * weigert te bestaan zonder client-id, en een uitzondering tijdens het opstarten
 * zou een wit scherm opleveren zonder enige uitleg. Nu draagt het inlogscherm de
 * melding, en gaat er intussen geen enkel credential mee.
 */
function resolveSetup(): Setup {
  if (!setup) {
    try {
      const provider = createTokenProvider();
      setTokenProvider(provider);
      setup = { provider, error: null };
    } catch (error) {
      setup = {
        provider: createAnonymousTokenProvider(),
        error: error instanceof Error ? error.message : String(error),
      };
    }
  }
  return setup;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { provider, error } = resolveSetup();
  const [status, setStatus] = useState<AuthStatus>(error ? "misconfigured" : "initialising");
  const [account, setAccount] = useState<AtlasAccount | null>(null);
  const [problem, setProblem] = useState<string | null>(error);

  useEffect(() => {
    if (error) return;
    let cancelled = false;

    provider
      .initialize()
      .then((resolved) => {
        if (cancelled) return;
        setAccount(resolved);
        setStatus(resolved ? "signed-in" : "signed-out");
      })
      .catch((failure: unknown) => {
        if (cancelled) return;
        setProblem(failure instanceof Error ? failure.message : String(failure));
        setStatus("misconfigured");
      });

    return () => {
      cancelled = true;
    };
  }, [provider, error]);

  // Het platform heeft het laatste woord over naam en rollen: een beheerder kan
  // de rol uit Entra vervangen, en de naam die het platform toont afwijken van
  // die in Microsoft (29-09-2026: het account info@bizzpower.com heet daar
  // "Abel Kikkert" en wordt gebruikt door Tom Kuin). Eén keer per aanmelding
  // ophalen; lukt het niet, dan blijft wat het token zei.
  const signedInSubject = status === "signed-in" ? account?.subject : undefined;
  useEffect(() => {
    if (!signedInSubject) return;
    let cancelled = false;
    fetchMe()
      .then((me) => {
        if (cancelled) return;
        setAccount((current) =>
          current
            ? {
                ...current,
                displayName: me.name ?? current.displayName,
                email: me.email ?? current.email,
                roles: me.roles,
                roleSource: me.role_source,
                steeringLayers: me.steering_layers ?? [],
              }
            : current,
        );
      })
      .catch(() => {
        /* De 401-afhandeling hieronder vangt een geweigerd token. Voor de
           sturingsrollen geldt dan "geen", zodat het dashboard niet blijft laden. */
        if (!cancelled) {
          setAccount((current) => (current && current.steeringLayers === undefined
            ? { ...current, steeringLayers: [] } : current));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [signedInSubject]);

  useEffect(() => {
    // Een 401 terwijl er iemand is ingelogd betekent dat de backend het token
    // niet accepteert. Meest waarschijnlijke oorzaak in deze opzet: het account
    // heeft nog geen `atlas_tenant_id` (ADR-003). De melding van de backend gaat
    // mee naar het scherm, want die noemt precies welke claim ontbreekt.
    setUnauthorizedHandler((detail) => {
      setStatus((current) => (current === "signed-in" ? "denied" : current));
      setProblem(detail);
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const signIn = useCallback(async () => {
    setProblem(null);
    try {
      await provider.signIn();
    } catch (failure) {
      setProblem(failure instanceof Error ? failure.message : String(failure));
    }
  }, [provider]);

  const signOut = useCallback(async () => {
    try {
      // Eerst de bewaarde antwoorden van de API wissen (Clear-Site-Data, plan
      // 17 E3), nu het token nog geldt; daarna pas uitloggen.
      await clearApiCache();
      resetDataVersion();
      await provider.signOut();
    } finally {
      // Ook als het uitloggen bij de identity provider mislukt, is de sessie
      // hier voorbij: liever een gebruiker die opnieuw moet inloggen dan een
      // scherm dat blijft doen alsof hij nog toegang heeft.
      setAccount(null);
      setStatus("signed-out");
      setProblem(null);
    }
  }, [provider]);

  const value = useMemo<AuthState>(
    () => ({
      status,
      mode: configuredAuthMode(),
      label: provider.label,
      account,
      problem,
      signIn,
      signOut,
    }),
    [status, provider.label, account, problem, signIn, signOut],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}
